import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaLock,
  FaCheck,
  FaArrowLeft,
  FaShieldAlt,
  FaEye,
  FaEyeSlash,
  FaKey,
  FaRocket,
} from "react-icons/fa";
import { HiOutlineSparkles } from "react-icons/hi";
import axios from "axios";
import toast from "react-hot-toast";

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [resetDone, setResetDone] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Check dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    };
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  // Check password strength
  useEffect(() => {
    const password = formData.password;
    let strength = 0;
    if (password.length >= 6) strength++;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    setPasswordStrength(Math.min(strength, 4));
  }, [formData.password]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      await axios.post(
        `/api/password-reset/reset-password/${token}`,
        {
          password: formData.password,
        },
      );

      setResetDone(true);
      toast.success("Password reset successful!");

      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength === 0) return "#ef4444";
    if (passwordStrength === 1) return "#f59e0b";
    if (passwordStrength === 2) return "#f59e0b";
    if (passwordStrength === 3) return "#10b981";
    return "#10b981";
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength === 0) return "Very Weak";
    if (passwordStrength === 1) return "Weak";
    if (passwordStrength === 2) return "Fair";
    if (passwordStrength === 3) return "Good";
    return "Strong";
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  const successVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: { type: "spring", stiffness: 400, damping: 20 },
    },
  };

  return (
    <div
      style={{
        minHeight: "calc(100vh - 200px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem",
        background: isDarkMode ? "#0f0f1a" : "#f8fafc",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated Background Particles */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 0,
        }}
      >
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.3, 0] }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              delay: Math.random() * 10,
            }}
            style={{
              position: "absolute",
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              width: Math.random() * 3 + 1,
              height: Math.random() * 3 + 1,
              background: `linear-gradient(135deg, #8b5cf6, #3b82f6)`,
              borderRadius: "50%",
              filter: "blur(2px)",
            }}
          />
        ))}
      </div>

      {/* Floating gradient orbs */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.1, 0.3],
        }}
        transition={{ duration: 8, repeat: Infinity }}
        style={{
          position: "absolute",
          top: "10%",
          right: "5%",
          width: 300,
          height: 300,
          background:
            "radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          pointerEvents: "none",
        }}
      />
      <motion.div
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.2, 0.1, 0.2],
        }}
        transition={{ duration: 10, repeat: Infinity, delay: 2 }}
        style={{
          position: "absolute",
          bottom: "10%",
          left: "5%",
          width: 250,
          height: 250,
          background:
            "radial-gradient(circle, rgba(59,130,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          pointerEvents: "none",
        }}
      />

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{
          maxWidth: "460px",
          width: "100%",
          background: isDarkMode
            ? "rgba(30, 30, 45, 0.9)"
            : "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(20px)",
          borderRadius: "32px",
          padding: "2.5rem",
          boxShadow: isDarkMode
            ? "0 25px 50px -12px rgba(0,0,0,0.5)"
            : "0 25px 50px -12px rgba(0,0,0,0.25)",
          border: `1px solid ${isDarkMode ? "rgba(139, 92, 246, 0.2)" : "rgba(139, 92, 246, 0.1)"}`,
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Animated gradient border top */}
        <motion.div
          animate={{
            background: [
              "linear-gradient(90deg, #8b5cf6, #3b82f6, #06b6d4, #8b5cf6)",
              "linear-gradient(90deg, #3b82f6, #06b6d4, #8b5cf6, #3b82f6)",
            ],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            borderRadius: "32px 32px 0 0",
          }}
        />

        <AnimatePresence mode="wait">
          {!resetDone ? (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                variants={itemVariants}
                style={{ textAlign: "center", marginBottom: "2rem" }}
              >
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "1rem",
                  }}
                >
                  <div
                    style={{
                      background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                      width: "60px",
                      height: "60px",
                      borderRadius: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FaKey size={28} color="white" />
                  </div>
                </motion.div>
                <h1
                  style={{
                    fontSize: "2rem",
                    marginBottom: "0.5rem",
                    background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    fontWeight: 700,
                  }}
                >
                  Reset Password
                </h1>
                <p style={{ color: isDarkMode ? "#94a3b8" : "#64748b" }}>
                  Enter your new password below
                </p>
              </motion.div>

              <form onSubmit={handleSubmit}>
                <motion.div
                  variants={itemVariants}
                  style={{ marginBottom: "1.5rem" }}
                >
                  <label
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      color: isDarkMode ? "#cbd5e1" : "#475569",
                      fontWeight: 500,
                      fontSize: "0.875rem",
                    }}
                  >
                    <FaLock
                      style={{ marginRight: "0.5rem", color: "#8b5cf6" }}
                    />
                    New Password
                  </label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      placeholder="Enter new password"
                      style={{
                        width: "100%",
                        padding: "0.9rem 1rem",
                        paddingRight: "3rem",
                        border: `2px solid ${isDarkMode ? "rgba(139, 92, 246, 0.2)" : "rgba(139, 92, 246, 0.1)"}`,
                        borderRadius: "16px",
                        outline: "none",
                        fontSize: "1rem",
                        transition: "all 0.3s",
                        backgroundColor: isDarkMode
                          ? "rgba(0,0,0,0.3)"
                          : "white",
                        color: isDarkMode ? "#f1f5f9" : "#1e293b",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                      onBlur={(e) => {
                        e.target.style.borderColor = isDarkMode
                          ? "rgba(139, 92, 246, 0.2)"
                          : "rgba(139, 92, 246, 0.1)";
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: "absolute",
                        right: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: isDarkMode ? "#94a3b8" : "#64748b",
                      }}
                    >
                      {showPassword ? (
                        <FaEyeSlash size={18} />
                      ) : (
                        <FaEye size={18} />
                      )}
                    </button>
                  </div>

                  {/* Password Strength Indicator */}
                  {formData.password && (
                    <div style={{ marginTop: "0.5rem" }}>
                      <div
                        style={{
                          height: "4px",
                          background: isDarkMode
                            ? "rgba(255,255,255,0.1)"
                            : "#e2e8f0",
                          borderRadius: "2px",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: `${(passwordStrength / 4) * 100}%`,
                            height: "100%",
                            background: getPasswordStrengthColor(),
                            transition: "width 0.3s ease",
                          }}
                        />
                      </div>
                      <p
                        style={{
                          fontSize: "0.7rem",
                          color: getPasswordStrengthColor(),
                          marginTop: "0.25rem",
                        }}
                      >
                        Password strength: {getPasswordStrengthText()}
                      </p>
                    </div>
                  )}
                </motion.div>

                <motion.div
                  variants={itemVariants}
                  style={{ marginBottom: "1.5rem" }}
                >
                  <label
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      color: isDarkMode ? "#cbd5e1" : "#475569",
                      fontWeight: 500,
                      fontSize: "0.875rem",
                    }}
                  >
                    <FaLock
                      style={{ marginRight: "0.5rem", color: "#8b5cf6" }}
                    />
                    Confirm Password
                  </label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      placeholder="Confirm new password"
                      style={{
                        width: "100%",
                        padding: "0.9rem 1rem",
                        paddingRight: "3rem",
                        border: `2px solid ${
                          formData.confirmPassword &&
                          formData.password !== formData.confirmPassword
                            ? "#ef4444"
                            : isDarkMode
                              ? "rgba(139, 92, 246, 0.2)"
                              : "rgba(139, 92, 246, 0.1)"
                        }`,
                        borderRadius: "16px",
                        outline: "none",
                        fontSize: "1rem",
                        transition: "all 0.3s",
                        backgroundColor: isDarkMode
                          ? "rgba(0,0,0,0.3)"
                          : "white",
                        color: isDarkMode ? "#f1f5f9" : "#1e293b",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                      onBlur={(e) => {
                        if (
                          formData.password === formData.confirmPassword ||
                          !formData.confirmPassword
                        ) {
                          e.target.style.borderColor = isDarkMode
                            ? "rgba(139, 92, 246, 0.2)"
                            : "rgba(139, 92, 246, 0.1)";
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      style={{
                        position: "absolute",
                        right: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: isDarkMode ? "#94a3b8" : "#64748b",
                      }}
                    >
                      {showConfirmPassword ? (
                        <FaEyeSlash size={18} />
                      ) : (
                        <FaEye size={18} />
                      )}
                    </button>
                  </div>

                  {/* Password mismatch warning */}
                  {formData.confirmPassword &&
                    formData.password !== formData.confirmPassword && (
                      <p
                        style={{
                          fontSize: "0.7rem",
                          color: "#ef4444",
                          marginTop: "0.25rem",
                        }}
                      >
                        Passwords do not match
                      </p>
                    )}
                  {formData.confirmPassword &&
                    formData.password === formData.confirmPassword &&
                    formData.password && (
                      <p
                        style={{
                          fontSize: "0.7rem",
                          color: "#10b981",
                          marginTop: "0.25rem",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.25rem",
                        }}
                      >
                        <FaCheck size={10} /> Passwords match
                      </p>
                    )}
                </motion.div>

                <motion.button
                  variants={itemVariants}
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: loading ? 1 : 1.02 }}
                  whileTap={{ scale: loading ? 1 : 0.98 }}
                  style={{
                    width: "100%",
                    padding: "1rem",
                    background: loading
                      ? "#94a3b8"
                      : "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    color: "white",
                    border: "none",
                    borderRadius: "16px",
                    fontSize: "1rem",
                    fontWeight: 600,
                    cursor: loading ? "not-allowed" : "pointer",
                    transition: "all 0.3s",
                    boxShadow: loading
                      ? "none"
                      : "0 4px 14px 0 rgba(139,92,246,0.4)",
                  }}
                >
                  {loading ? "Resetting..." : "Reset Password"}
                </motion.button>
              </form>
            </motion.div>
          ) : (
            <motion.div
              key="success"
              variants={successVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              style={{ textAlign: "center" }}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  type: "spring",
                  stiffness: 400,
                  damping: 20,
                  delay: 0.2,
                }}
                style={{
                  width: "80px",
                  height: "80px",
                  background: "linear-gradient(135deg, #10b981, #059669)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 1.5rem",
                  color: "white",
                  fontSize: "2.5rem",
                  boxShadow: "0 10px 25px -5px rgba(16,185,129,0.4)",
                }}
              >
                <FaCheck />
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                style={{
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  marginBottom: "1rem",
                  fontWeight: 700,
                }}
              >
                Password Reset!
              </motion.h2>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                style={{
                  color: isDarkMode ? "#94a3b8" : "#64748b",
                  marginBottom: "1rem",
                }}
              >
                Your password has been successfully reset.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                  marginTop: "1rem",
                }}
              >
                <FaRocket size={16} color="#8b5cf6" />
                <p style={{ color: isDarkMode ? "#94a3b8" : "#64748b" }}>
                  Redirecting to login...
                </p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div
          variants={itemVariants}
          style={{ textAlign: "center", marginTop: "1.5rem" }}
        >
          <Link
            to="/login"
            style={{
              color: isDarkMode ? "#94a3b8" : "#64748b",
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              fontSize: "0.875rem",
              transition: "color 0.3s",
            }}
            onMouseEnter={(e) => (e.target.style.color = "#8b5cf6")}
            onMouseLeave={(e) =>
              (e.target.style.color = isDarkMode ? "#94a3b8" : "#64748b")
            }
          >
            <FaArrowLeft size={12} /> Back to Login
          </Link>
        </motion.div>

        {/* Security Badge */}
        <motion.div
          variants={itemVariants}
          style={{
            marginTop: "1.5rem",
            paddingTop: "1rem",
            borderTop: `1px solid ${isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)"}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
          }}
        >
          <FaShieldAlt size={12} color="#10b981" />
          <span
            style={{
              color: isDarkMode ? "#94a3b8" : "#64748b",
              fontSize: "0.7rem",
            }}
          >
            Secure password reset with 256-bit encryption
          </span>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ResetPassword;

